import React from 'react'
import { colors } from './fieldTheme'
import { doneCountLabel, greetingLabel, tourStartedByLabel } from './fieldLabels'
import { pitchDoneCount } from './fieldTours'
import { ArrivalIllustration, DepartureIllustration, TourIllustration, FreeIcon, TourIcon, ChevronRight, DotsIcon, HistoryIcon } from './FieldIcons'

// Accueil terrain (canvas W1, et W2 quand un tour est en cours). Décisions 21, 22, 35 et 50 :
// un seul tour en cours à la fois, « Tour de terrain » n'apparaît que s'il n'y en a aucun.

function BigCard({ title, subtitle, color, Illustration, compact, disabled, onClick })
{
	const pastille = compact ? 96 : 112
	return (
		<button
			type="button"
			className="field-press"
			onClick={onClick}
			disabled={disabled}
			style={{
				backgroundImage: 'none',
				backgroundColor: colors.surface,
				flex: 1,
				minWidth: 0,
				minHeight: compact ? 200 : 248,
				border: '2px solid ' + colors.border,
				borderRadius: 24,
				boxShadow: '0 3px 0 ' + colors.borderShadow,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: compact ? 8 : 10,
				padding: 20,
				textAlign: 'center',
				color: colors.text,
				fontFamily: 'inherit',
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled ? 0.45 : 1
			}}
		>
			<span style={{ width: pastille, height: pastille, borderRadius: 999, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				<Illustration size={compact ? 66 : 76} />
			</span>
			<span style={{ fontSize: compact ? 22 : 24, fontWeight: 800, lineHeight: 1.15 }}>{title}</span>
			<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, lineHeight: 1.3 }}>{subtitle}</span>
		</button>
	)
}

const ActivityIcon = () => <HistoryIcon size={26} color="#FFFFFF" />

function OtherRow({ title, subtitle, color, Icon, disabled, onClick })
{
	return (
		<button
			type="button"
			className="field-row"
			onClick={onClick}
			disabled={disabled}
			style={{
				backgroundImage: 'none',
				backgroundColor: colors.surface,
				minHeight: 72,
				display: 'flex',
				alignItems: 'center',
				gap: 14,
				padding: '10px 20px',
				width: '100%',
				border: 0,
				textAlign: 'left',
				color: colors.text,
				fontFamily: 'inherit',
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled ? 0.45 : 1
			}}
		>
			<span style={{ width: 44, height: 44, borderRadius: 999, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				<Icon />
			</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 19, fontWeight: 700 }}>{title}</span>
				<span style={{ fontSize: 15, color: colors.textSecondary, fontWeight: 500 }}>{subtitle}</span>
			</span>
			<ChevronRight />
		</button>
	)
}

function TourCard({ tour, user, pitches, onResume, onOptions })
{
	return (
		<div style={{ background: colors.surface, borderRadius: 24, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, border: '3px solid ' + colors.tour }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
				<span style={{ fontSize: 15, fontWeight: 700, color: colors.primary, letterSpacing: '.04em', textTransform: 'uppercase' }}>Tour en cours</span>
				<button
					type="button"
					aria-label="Options du tour"
					onClick={onOptions}
					style={{ backgroundImage: 'none', backgroundColor: 'transparent', width: 48, height: 48, borderRadius: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: colors.primary, border: '2px solid ' + colors.border, flex: 'none', marginRight: -6, padding: 0, cursor: 'pointer' }}
				>
					<DotsIcon />
				</button>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
				<span style={{ width: 68, height: 68, borderRadius: 999, background: colors.tour, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
					<TourIllustration size={48} />
				</span>
				<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
					<span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{tour.name}</span>
					<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{doneCountLabel(pitchDoneCount(tour, pitches))}</span>
					<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{tourStartedByLabel(tour, user)}</span>
				</span>
			</div>
			<button
				type="button"
				className="field-solid"
				onClick={onResume}
				style={{ backgroundImage: 'none', backgroundColor: colors.tour, marginTop: 4, minHeight: 56, borderRadius: 18, border: 0, color: colors.text, fontSize: 19, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', fontFamily: 'inherit', cursor: 'pointer' }}
			>
				Reprendre le tour
				<ChevronRight color="currentColor" strokeWidth={2.8} />
			</button>
		</div>
	)
}

// hiddenTour : tour en cours masqué de mon accueil, qui reste accessible depuis Autres inventaires (décision 22)
export default function FieldHome({ user, tour, hiddenTour, pitches, canWrite, canStartTour, onStart, onResumeTour, onTourOptions, onActivity })
{
	const hasTour = !!tour
	const readOnly = !canWrite
	const cards = (
		<div style={{ display: 'flex', gap: 16 }}>
			<BigCard title="Entrée" subtitle="Inventaire d'arrivée" color={colors.arrival} Illustration={ArrivalIllustration} compact={hasTour} disabled={readOnly} onClick={() => onStart('arrival')} />
			<BigCard title="Sortie" subtitle="Inventaire de départ" color={colors.departure} Illustration={DepartureIllustration} compact={hasTour} disabled={readOnly} onClick={() => onStart('departure')} />
		</div>
	)

	return (
		<div style={{ paddingBottom: 32 }}>
			<h1 style={{ fontSize: 28, lineHeight: 1.2, fontWeight: 800, color: colors.primary, padding: hasTour ? '24px 20px 0' : '24px 20px 4px' }}>Inventaires</h1>
			{!hasTour && (
				<p style={{ padding: '0 20px', color: colors.textSecondary, fontSize: 18, fontWeight: 500 }}>{greetingLabel(user)}</p>
			)}
			{readOnly && (
				<p style={{ padding: '12px 20px 0', color: colors.textSecondary, fontSize: 17, fontWeight: 600 }}>
					Vous pouvez consulter les inventaires, mais pas en faire. Demandez le droit d'écriture à votre responsable.
				</p>
			)}

			{hasTour ? (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 20px 0' }}>
					<TourCard tour={tour} user={user} pitches={pitches} onResume={onResumeTour} onOptions={onTourOptions} />
					{cards}
				</div>
			) : (
				<div style={{ padding: '32px 20px 0' }}>{cards}</div>
			)}

			<h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', padding: '32px 20px 12px' }}>Autres inventaires</h2>
			<div style={{ margin: '0 20px', display: 'flex', flexDirection: 'column', background: colors.surface, borderRadius: 18, overflow: 'hidden' }}>
				<OtherRow title="Hors séjour" subtitle="Mobil-home propriétaire, constat isolé" color={colors.free} Icon={FreeIcon} disabled={readOnly} onClick={() => onStart('free')} />
				<span aria-hidden="true" style={{ display: 'block', height: 1, background: colors.separator, marginLeft: 78 }} />
				{hiddenTour && <OtherRow title={'Reprendre : ' + hiddenTour.name} subtitle={doneCountLabel(pitchDoneCount(hiddenTour, pitches))} color={colors.tour} Icon={TourIcon} disabled={readOnly} onClick={onResumeTour} />}
				{canStartTour && !hasTour && !hiddenTour && <OtherRow title="Tour de terrain" subtitle="Hiver, avant ouverture" color={colors.tour} Icon={TourIcon} disabled={readOnly} onClick={() => onStart('tour')} />}
			</div>

			{onActivity && (
				<React.Fragment>
					<h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', padding: '32px 20px 12px' }}>Suivi</h2>
					<div style={{ margin: '0 20px', display: 'flex', flexDirection: 'column', background: colors.surface, borderRadius: 18, overflow: 'hidden' }}>
						<OtherRow title="Mon activité" subtitle="Mes inventaires, tours et interventions" color={colors.primary} Icon={ActivityIcon} onClick={onActivity} />
					</div>
				</React.Fragment>
			)}
		</div>
	)
}
